/**
 * Currency utility functions for handling multi-currency support
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  decimalPlaces: number;
}

/**
 * Supported currencies with their symbols and formatting information
 */
export const SUPPORTED_CURRENCIES: Record<string, CurrencyInfo> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    decimalPlaces: 2
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    decimalPlaces: 2
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    decimalPlaces: 2
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    decimalPlaces: 2
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    decimalPlaces: 2
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen',
    decimalPlaces: 0
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    name: 'Swiss Franc',
    decimalPlaces: 2
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    name: 'Chinese Yuan',
    decimalPlaces: 2
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    decimalPlaces: 2
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'Brazilian Real',
    decimalPlaces: 2
  },
  MXN: {
    code: 'MXN',
    symbol: '$',
    name: 'Mexican Peso',
    decimalPlaces: 2
  },
  KRW: {
    code: 'KRW',
    symbol: '₩',
    name: 'South Korean Won',
    decimalPlaces: 0
  }
};

/**
 * Get currency information by currency code
 */
export function getCurrencyInfo(currencyCode: string): CurrencyInfo {
  return SUPPORTED_CURRENCIES[currencyCode.toUpperCase()] || SUPPORTED_CURRENCIES.USD;
}

/**
 * Get currency symbol by currency code
 */
export function getCurrencySymbol(currencyCode: string): string {
  return getCurrencyInfo(currencyCode).symbol;
}

/**
 * Format amount with currency symbol
 */
export function formatCurrency(amount: number, currencyCode: string = 'USD'): string {
  const currency = getCurrencyInfo(currencyCode);
  const formattedAmount = amount.toFixed(currency.decimalPlaces);
  
  // Add thousand separators
  const parts = formattedAmount.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return `${currency.symbol}${parts.join('.')}`;
}

/**
 * Format amount with currency symbol positioned after the amount (for some currencies)
 */
export function formatCurrencyAfter(amount: number, currencyCode: string = 'USD'): string {
  const currency = getCurrencyInfo(currencyCode);
  const formattedAmount = amount.toFixed(currency.decimalPlaces);
  
  // Add thousand separators
  const parts = formattedAmount.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Some currencies put the symbol after the amount
  const currenciesAfterSymbol = ['EUR', 'GBP', 'JPY', 'KRW'];
  if (currenciesAfterSymbol.includes(currencyCode.toUpperCase())) {
    return `${parts.join('.')} ${currency.symbol}`;
  }
  
  return `${currency.symbol}${parts.join('.')}`;
}

/**
 * Get list of supported currency codes
 */
export function getSupportedCurrencyCodes(): string[] {
  return Object.keys(SUPPORTED_CURRENCIES);
}

/**
 * Get list of supported currencies for dropdowns
 */
export function getSupportedCurrencies(): Array<{ value: string; label: string; symbol: string }> {
  return Object.values(SUPPORTED_CURRENCIES).map(currency => ({
    value: currency.code,
    label: `${currency.name} (${currency.code})`,
    symbol: currency.symbol
  }));
}

/**
 * Validate currency code
 */
export function isValidCurrencyCode(currencyCode: string): boolean {
  return currencyCode.toUpperCase() in SUPPORTED_CURRENCIES;
}

/**
 * Parse currency amount from string (removes currency symbols and formatting)
 */
export function parseCurrencyAmount(value: string, currencyCode: string = 'USD'): number {
  const currency = getCurrencyInfo(currencyCode);
  
  // Remove currency symbols and non-numeric characters except decimal point
  let cleanValue = value.replace(/[^\d.-]/g, '');
  
  // Handle negative numbers
  const isNegative = cleanValue.startsWith('-');
  cleanValue = cleanValue.replace(/^-/, '');
  
  // Parse the number
  const amount = parseFloat(cleanValue) || 0;
  
  return isNegative ? -amount : amount;
}
